import { globalVariables } from '../config/globalVariables';

import { kcGateway } from './keycloak';

interface NeoIdTokenResponse {
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
  expires_in?: number;
  link?: string;
}

enum ExchangeResult {
  Success,
  Fail,
  Redirecting,
}

async function exchangeEolsTokenForNeoId(
  eolsToken: string,
  deepLinkGuid: string
): Promise<ExchangeResult> {
  const url = `${globalVariables.caDomain}api/v13/lms/neoid-token-exchange/`;

  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'X-EOLS-Authorization': eolsToken,
        'X-EOLS-Deeplink': deepLinkGuid,
      },
    });

    if (!resp.ok) {
      console.error(`NeoID token exchange failed: ${resp.status}`);

      return ExchangeResult.Fail;
    }

    const data: NeoIdTokenResponse = await resp.json();

    if (data.link) {
      window.location.assign(data.link);

      // Stop further init flow while browser is navigating to the target link.
      return ExchangeResult.Redirecting;
    }

    if (!data.access_token) {
      console.error('NeoID token exchange response is missing access_token');

      return ExchangeResult.Fail;
    }

    await kcGateway.setUserWithToken(
      data.access_token,
      data.expires_in!,
      data.id_token,
      data.refresh_token
    );

    return ExchangeResult.Success;
  } catch (err) {
    console.error('NeoID token exchange failed:', err);

    return ExchangeResult.Fail;
  }
}

export async function resolveLMSDeepLink() {
  const link = window.location.href;

  // Expected LMS redirect format:
  // #/login/<something>?courseId=<id>&token=<token>&deepLinkGuid=<guid> or login/614?&token=<token>&deepLinkGuid=<guid>
  if (!link.includes('login/')) {
    return false;
  }

  const qIndex = link.indexOf('?');
  const queryString = qIndex >= 0 ? link.substring(qIndex + 1) : '';

  location.hash = '';

  const params = new URLSearchParams(queryString);
  const token = params.get('token');
  const deepLinkGuid = params.get('deepLinkGuid');
  const courseId = params.get('courseId');

  if (!token || !deepLinkGuid) {
    return false;
  }

  if (!courseId) {
    console.warn('LMS deep link is missing courseId');
  }

  const exchanged = await exchangeEolsTokenForNeoId(token, deepLinkGuid);

  if (exchanged === ExchangeResult.Fail) {
    return false;
  } else if (exchanged === ExchangeResult.Redirecting) {
    // Token exchange flow will handle redirection, stop further init.
    return true;
  }

  // courseId is present in LMS URL but for now it's hardcoded to courseSectionId=614.
  const paramsForRequest = new URLSearchParams({
    courseSectionId: '614',
    deepLinkGuid,
  });
  const url = `/api/v2/deeplinks?${paramsForRequest.toString()}`;

  try {
    const resp = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!resp.ok) {
      throw new Error(`EOLS API error: ${resp.status}`);
    }

    const data = await resp.json();
    const deepLinkData = Array.isArray(data?.deepLinks)
      ? data.deepLinks[0]
      : data?.deepLinks;
    const isLocked = data?.locked === true || deepLinkData?.locked === true;

    if (isLocked) {
      console.warn('EOLS deep link is locked');

      return false;
    }

    const contentKey = deepLinkData?.parameters?.contentKey;
    const contentName = deepLinkData?.parameters?.contentName;

    if (!contentKey || !contentName) {
      console.error('EOLS response missing contentKey/contentName');

      return false;
    }

    const caPath = `/screens/${encodeURIComponent(contentKey)}/${encodeURIComponent(
      contentName
    )}`;

    window.location.assign(caPath);

    return true;
  } catch (err) {
    console.error('Deep link resolution failed:', err);

    return false;
  }
}
